import "./CHeader.scss";
import CILPRON from "../../../../Statics/Images/CPN.svg";
import React, { useEffect, useState, useRef } from "react";
import { Avatar, Button, Menu, MenuItem, MenuList, MenuPopover, MenuTrigger, Popover, PopoverSurface, PopoverTrigger } from "@fluentui/react-components";
import Messages from "../../../Communication/Message";
import { Comment24Regular, Grid24Regular, Person24Regular, PersonAccounts24Regular, PersonAdd24Regular, Search24Regular, SignOut24Regular } from "@fluentui/react-icons";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../../../hooks/useAuth";

const NAV_ITEMS = [
  {
    label: "Products",
    sub: [
      { heading: "AI & Automation", links: ["Artificial Intelligence"] },
      { heading: "Business Tools", links: ["CRM", "Accounting", "Email & Calendars", "SwiftGrid Email API"] },
      { heading: "Industry Solutions", links: ["Hospitality & PMS", "Academic Portals"] },
    ],
  },
  { label: "Channels", href: "/channels", sub: null },
  {
    label: "Industries",
    href: "/Industries", sub: null
    // sub: [
    //   {
    //     heading: "By Industry",
    //     links: ["Hospitality", "Education"],
    //   },
    //   {
    //     heading: "By Product",
    //     links: ["CRM", "Accounting", "Swift Grid Email API", "Email & Calendar"],
    //   },
    //   {
    //     heading: "By Role",
    //     links: ["IT Leaders", "Operations", "Finance Teams", "HR & Admin"],
    //   },
    // ],
  },
  { label: "About", href: "/about", sub: null },       // who you are — matters for early adopters
  { label: "Contact", href: "/contact", sub: null },
];
// ── SVG icons ─────────────────────────────────────────────────
const IconSearch: React.FC = () => (
  <svg width="15" height="44" viewBox="0 0 15 44" fill="currentColor" aria-hidden="true">
    <path d="M14.298 27.202l-3.87-3.87a5.55 5.55 0 1 0-1.122 1.122l3.87 3.87a.775.775 0 1 0 1.096-1.097v-.025zM1.55 20a4.45 4.45 0 1 1 8.9 0 4.45 4.45 0 0 1-8.9 0z" />
  </svg>
);

const IconComment: React.FC = () => (
  <svg width="17" height="44" viewBox="0 0 17 44" fill="currentColor" aria-hidden="true">
    <path d="M8.5 6C4.9 6 2 8.6 2 11.8c0 1.8.9 3.4 2.4 4.5L3.5 19l3.1-1.4a7 7 0 0 0 1.9.2c3.6 0 6.5-2.6 6.5-5.8S12.1 6 8.5 6z" />
  </svg>
);

const IconChevronDown: React.FC = () => (
  <svg width="9" height="9" viewBox="0 0 9 6" fill="currentColor" aria-hidden="true">
    <path d="M4.673 5.009 2.017 2.342a.445.445 0 0 1-.123-.304.433.433 0 0 1 .433-.433.447.447 0 0 1 .316.135L5 4.105l2.356-2.365a.458.458 0 0 1 .316-.135.433.433 0 0 1 .433.433.419.419 0 0 1-.123.3L5.327 5.009a.445.445 0 0 1-.327.14.464.464 0 0 1-.327-.14z" />
  </svg>
);

const IconChevronBack: React.FC = () => (
  <svg width="9" height="48" viewBox="0 0 9 48" fill="currentColor" aria-hidden="true">
    <path d="m1.562 24.062 6.558-6.424a.6.6 0 1 0-.849-.857l-7 6.857a.6.6 0 0 0-.003.862l7 6.724a.6.6 0 1 0 .831-.865z" />
  </svg>
);

const IconChevronRight: React.FC = () => (
  <svg width="9" height="48" viewBox="0 0 9 48" fill="currentColor" aria-hidden="true">
    <path d="m7.438 24.062-6.558-6.424a.6.6 0 1 1 .849-.857l7 6.857a.6.6 0 0 1 .003.862l-7 6.724a.6.6 0 1 1-.831-.865z" />
  </svg>
);

// ─────────────────────────────────────────────────────────────
const CHeader: React.FC = () => {
const navigate = useNavigate();
  const { user, isAuthenticated, logout } = useAuth();

  const [openFlyout, setOpenFlyout]   = useState<string | null>(null);
  const [mobileOpen, setMobileOpen]   = useState(false);
  const [mobileSub, setMobileSub]     = useState<string | null>(null);
  const [scrolled, setScrolled]       = useState(false);
  const [chatOpen, setChatOpen]       = useState(false);
  const [searchOpen, setSearchOpen]   = useState(false);
  const searchInputRef                = useRef<HTMLInputElement>(null);
  const closeTimer                    = useRef<ReturnType<typeof setTimeout> | null>(null);
  const anyPanelOpen                  = Boolean(openFlyout) || searchOpen;

  const handleLogout = () => {
    logout();
    navigate("/");
  };
  
  // scroll detection
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // close flyout / search when clicking outside the nav
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      const nav = document.getElementById("cn-nav");
      if (nav && !nav.contains(e.target as Node)) {
        setOpenFlyout(null);
        setSearchOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  // ── Apple's html.globalnav-noscroll ──────────────────────────
  // Lock page scroll whenever ANY panel is open (desktop flyout / search OR
  // the mobile menu). This is what stops the page scrolling THROUGH behind an
  // open panel. We measure the scrollbar width and pad <body> by it so the
  // content doesn't jump sideways when the scrollbar disappears.
  useEffect(() => {
    const lock = anyPanelOpen || mobileOpen;
    const root = document.documentElement;
    if (lock) {
      const sbw = window.innerWidth - root.clientWidth;
      if (sbw > 0) root.style.setProperty("--cn-scrollbar-width", `${sbw}px`);
      root.classList.add("cn-noscroll");
    } else {
      root.classList.remove("cn-noscroll");
      root.style.removeProperty("--cn-scrollbar-width");
    }
    return () => {
      root.classList.remove("cn-noscroll");
      root.style.removeProperty("--cn-scrollbar-width");
    };
  }, [anyPanelOpen, mobileOpen]);

  // focus the search input whenever search opens
  useEffect(() => {
    if (searchOpen) {
      const t = setTimeout(() => searchInputRef.current?.focus(), 50);
      return () => clearTimeout(t);
    }
  }, [searchOpen]);

  // close search / flyout / mobile menu on Escape
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        setSearchOpen(false);
        setOpenFlyout(null);
        setMobileOpen(false);
        setMobileSub(null);
      }
    };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, []);

  // clean up the close timer on unmount (prevents setState-after-unmount)
  useEffect(() => {
    return () => {
      if (closeTimer.current) clearTimeout(closeTimer.current);
    };
  }, []);

  const cancelClose = () => {
    if (closeTimer.current) { clearTimeout(closeTimer.current); closeTimer.current = null; }
  };

  const scheduleClose = () => {
    cancelClose();
    closeTimer.current = setTimeout(() => setOpenFlyout(null), 150);
  };

  const openMenu = (label: string) => {
    cancelClose();
    setSearchOpen(false);
    setOpenFlyout(label);
  };

  const toggleSearch = () => {
    setSearchOpen((v) => {
      if (!v) setOpenFlyout(null);
      return !v;
    });
  };

  const closeAllPanels = () => {
    setOpenFlyout(null);
    setSearchOpen(false);
  };

  const closeMobile = () => {
    setMobileOpen(false);
    setMobileSub(null);
  };

  const activeFlyoutItem = openFlyout
    ? NAV_ITEMS.find((i) => i.label === openFlyout)
    : null;

  return (
    <>
      {/* ── Nav bar ───────────────────────────────────────── */}
      <nav
        id="cn-nav"
        className={[
          "cn-nav",
          scrolled ? "cn-nav--scrolled" : "",
          anyPanelOpen ? "cn-nav--flyout-open" : "",
          mobileOpen ? "cn-nav--mobile-open" : "",
        ].filter(Boolean).join(" ")}
      >
        <div className="cn-nav__bar">

          {/* Logo */}
          <a href="/" className="cn-nav__logo" aria-label="Cilpron Home">
            <img src={CILPRON} alt="Cilpron" className="cn-nav__logo-img" />
          </a>

          {/* Desktop links */}
          <ul className="cn-nav__links" onMouseLeave={scheduleClose}>
            {NAV_ITEMS.map((item) => (
              <li
                key={item.label}
                className={`cn-nav__item${openFlyout === item.label ? " cn-nav__item--open" : ""}`}
                onMouseEnter={() => item.sub ? openMenu(item.label) : scheduleClose()}
              >
                {item.href ? (
                  <a href={item.href} className="cn-nav__link">
                    {item.label}
                  </a>
                ) : (
                  <button
                    className="cn-nav__link"
                    aria-expanded={openFlyout === item.label}
                    aria-haspopup={item.sub ? "menu" : undefined}
                    onClick={(e) => {
                      e.stopPropagation();
                      if (item.sub) {
                        openFlyout === item.label ? setOpenFlyout(null) : openMenu(item.label);
                      }
                    }}
                  >
                    <span className="cn-nav__link-text">{item.label}</span>
                    {item.sub && (
                      <span
                        className={`cn-nav__chevron${openFlyout === item.label ? " cn-nav__chevron--open" : ""}`}
                      >
                        <IconChevronDown />
                      </span>
                    )}
                  </button>
                )}
              </li>
            ))}
          </ul>

          {/* Desktop actions */}
          <div className="cn-nav__actions">
            <button
              className={`cn-nav__icon-btn${searchOpen ? " cn-nav__icon-btn--active" : ""}`}
              aria-label={searchOpen ? "Close search" : "Search"}
              aria-expanded={searchOpen}
              onClick={toggleSearch}
            >
              {searchOpen ? (
                <svg width="14" height="14" viewBox="0 0 14 14" fill="currentColor" aria-hidden="true">
                  <path d="M13 1L1 13M1 1l12 12" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" fill="none" />
                </svg>
              ) : (
                <Search24Regular />
              )}
            </button>
            {/* <button className="cn-nav__icon-btn" aria-label="Chat" onClick={() => setChatOpen((v) => !v)}>
              <Comment24Regular />
            </button> */}
         <Popover withArrow inline positioning="below-end">
  <PopoverTrigger disableButtonEnhancement>
    <Button
      className="cn-nav__icon-btn cn-nav__avatar-btn"
      aria-label="Account"
      appearance="transparent"
      icon={
        isAuthenticated && user ? (
          <Avatar
            size={28}
            name={`${user.firstName} ${user.lastName}`}
            color="brand"
          />
        ) : (
          <PersonAccounts24Regular />
        )
      }
    />
  </PopoverTrigger>

  <PopoverSurface
    style={{
      padding: "4px",
      minWidth: "200px",
      background: "rgba(255, 255, 255, 0.96)",
      backdropFilter: "blur(12px)",
      WebkitBackdropFilter: "blur(12px)",
      border: "1px solid rgba(255, 255, 255, 0.4)",
      boxShadow: "0 8px 32px rgba(0, 0, 0, 0.12)",
    }}
  >
    {isAuthenticated && user ? (
      <MenuList>
        <div style={{ padding: "8px 12px 10px", borderBottom: "1px solid rgba(0,0,0,0.06)", marginBottom: "4px" }}>
          <div style={{ fontSize: "13px", fontWeight: 600, color: "#1d1d1f" }}>
            {user.firstName} {user.lastName}
          </div>
          <div style={{ fontSize: "11px", color: "#6e6e73", marginTop: "2px" }}>
            {user.email}
          </div>
          <div style={{ fontSize: "10px", color: "#8e8e93", marginTop: "2px", textTransform: "uppercase", letterSpacing: "0.04em" }}>
            {user.role}
          </div>
        </div>
        <MenuItem icon={<Grid24Regular />} onClick={() => navigate("/dashboard")}>
          Dashboard
        </MenuItem>
        <MenuItem icon={<Person24Regular />} onClick={() => navigate("/profile")}>
          My Profile
        </MenuItem>
        <MenuItem icon={<SignOut24Regular />} onClick={handleLogout}>
          Sign out
        </MenuItem>
      </MenuList>
    ) : (
      <MenuList>
        <MenuItem icon={<Person24Regular />} onClick={() => navigate("/auth/login")}>
          Log in
        </MenuItem>
        <MenuItem icon={<PersonAdd24Regular />} onClick={() => navigate("/auth/signup")}>
          Sign up
        </MenuItem>
      </MenuList>
    )}
  </PopoverSurface>
</Popover>
          </div>

          {/* Hamburger (mobile) */}
          <button
            className={`cn-nav__hamburger${mobileOpen ? " cn-nav__hamburger--open" : ""}`}
            aria-label={mobileOpen ? "Close menu" : "Open menu"}
            aria-expanded={mobileOpen}
            onClick={() => {
              setMobileOpen((v) => {
                if (!v) { setChatOpen(false); setSearchOpen(false); setOpenFlyout(null); }
                return !v;
              });
              setMobileSub(null);
            }}
          >
            <svg width="18" height="18" viewBox="0 0 18 18" aria-hidden="true">
              <polyline
                className={`cn-ham-top${mobileOpen ? " cn-ham--open" : ""}`}
                fill="none" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round"
                points="2 5, 16 5"
              />
              <polyline
                className={`cn-ham-bottom${mobileOpen ? " cn-ham--open" : ""}`}
                fill="none" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round"
                points="2 13, 16 13"
              />
            </svg>
          </button>
        </div>

        {/* ── Nav flyout panel ────────────────────────────── */}
        {activeFlyoutItem?.sub && (
          <div
            className="cn-flyout"
            onMouseEnter={cancelClose}
            onMouseLeave={scheduleClose}
          >
            <div className="cn-flyout__inner">
              {activeFlyoutItem.sub.map((group) => (
                <div key={group.heading} className="cn-flyout__group">
                  <p className="cn-flyout__heading">{group.heading}</p>
                  <ul className="cn-flyout__list">
                    {group.links.map((link) => (
                      <li key={link} className="cn-flyout__list-item">
                        <a href={`/${link.toLowerCase().replace(/\s+/g, "-")}`} className="cn-flyout__link">
                          {link}
                        </a>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* ── Search flyout panel ──────────────────────────── */}
        {searchOpen && (
          <div className="cn-search-flyout" role="search">
            <div className="cn-search-flyout__inner">
              <div className="cn-search-flyout__row">

                {/* Pill: icon + input */}
                <div className="cn-search-flyout__pill">
                  <span className="cn-search-flyout__icon">
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor" aria-hidden="true">
                      <path d="M15.25 14.19l-3.76-3.76A6 6 0 1 0 10.19 11.5l3.76 3.76a.75.75 0 0 0 1.3-.53.74.74 0 0 0-.22-.54zM2 7a5 5 0 1 1 5 5 5 5 0 0 1-5-5z" />
                    </svg>
                  </span>
                  <input
                    ref={searchInputRef}
                    className="cn-search-flyout__input"
                    type="search"
                    placeholder="Search…"
                    aria-label="Search"
                    autoComplete="off"
                    autoCorrect="off"
                    autoCapitalize="off"
                    spellCheck={false}
                  />
                </div>

                {/* Cancel */}
                <button
                  className="cn-search-flyout__cancel"
                  onClick={() => setSearchOpen(false)}
                  aria-label="Cancel search"
                >
                  Cancel
                </button>

              </div>

              {/* Hint shown only on mobile below the pill */}
              <p className="cn-search-flyout__hint">
                Type to search products, solutions, and resources.
              </p>

            </div>
          </div>
        )}

        {/* ── Mobile full-screen menu ──────────────────────── */}
        <div className={`cn-mobile-menu${mobileOpen ? " cn-mobile-menu--open" : ""}`}>
          <div className="cn-mobile-menu__scroll">

            {/* Back button row — only shown when a submenu is open */}
            {mobileSub && (
              <div className="cn-mobile-menu__menuback">
                <button
                  className="cn-mobile-menu__back-btn"
                  aria-label="Main menu"
                  onClick={() => setMobileSub(null)}
                >
                  <IconChevronBack />
                </button>
              </div>
            )}

            {/* Root list */}
            {!mobileSub && (
              <ul className="cn-mobile-menu__list">
                {NAV_ITEMS.map((item) => (
                  <li key={item.label} className="cn-mobile-menu__item">
                    {item.sub ? (
                      <button
                        className="cn-mobile-menu__link"
                        onClick={() => setMobileSub(item.label)}
                      >
                        <span className="cn-mobile-menu__link-text">{item.label}</span>
                        <span className="cn-mobile-menu__link-chevron">
                          <IconChevronRight />
                        </span>
                      </button>
                    ) : (
                      <a
                        href={item.href || "#"}
                        className="cn-mobile-menu__link"
                        onClick={closeMobile}
                      >
                        <span className="cn-mobile-menu__link-text">{item.label}</span>
                      </a>
                    )}
                  </li>
                ))}
              </ul>
            )}

            {/* Submenu drill-down */}
            {mobileSub && (() => {
              const item = NAV_ITEMS.find((i) => i.label === mobileSub);
              if (!item?.sub) return null;
              return (
                <div className="cn-mobile-submenu">
                  <p className="cn-mobile-submenu__title">{mobileSub}</p>
                  {item.sub.map((group) => (
                    <div key={group.heading} className="cn-mobile-submenu__group">
                      <p className="cn-mobile-submenu__heading">{group.heading}</p>
                      <ul className="cn-mobile-submenu__list">
                        {group.links.map((link) => (
                          <li key={link}>
                            <a
                              href={`/${link.toLowerCase().replace(/\s+/g, "-")}`}
                              className="cn-mobile-submenu__link"
                              onClick={closeMobile}
                            >
                              {link}
                            </a>
                          </li>
                        ))}
                      </ul>
                    </div>
                  ))}
                </div>
              );
            })()}

            {/* Action icons — always at the bottom */}
            <div className="cn-mobile-menu__actions">
              <button
                className="cn-mobile-menu__action-btn"
                aria-label="Search"
                onClick={() => { closeMobile(); toggleSearch(); }}
              >
                <IconSearch />
              </button>
            
              <Menu inline positioning="below-end">
                <MenuTrigger disableButtonEnhancement>
                  <Button
                    className="cn-mobile-menu__action-btn"
                    aria-label="Account"
                    appearance="transparent"
                    icon={<Avatar size={28} />}
                  />
                </MenuTrigger>
                <MenuPopover>
                  <MenuList>
                    <MenuItem icon={<Person24Regular />} onClick={() => {/* open login */}}>
                      Log in
                    </MenuItem>
                    <MenuItem icon={<PersonAdd24Regular />} onClick={() => {/* open signup */}}>
                      Sign up
                    </MenuItem>
                  </MenuList>
                </MenuPopover>
              </Menu>
            </div>
          </div>
        </div>
      </nav>

      {/* ── Curtain ────────────────────────────────────────────
          Apple's .globalnav-curtain. A fixed, blurred full-screen backdrop
          that sits BELOW the nav (9999) but ABOVE the page. Fades in with any
          open desktop panel; clicking it closes everything. Hidden on mobile,
          where the full-screen menu is its own backdrop. */}
      <div
        className={`cn-curtain${anyPanelOpen ? " cn-curtain--visible" : ""}`}
        onClick={closeAllPanels}
        aria-hidden="true"
      />

      {/* Spacer so page content sits below the fixed bar */}
      <div className="cn-nav__spacer" />

      {/* Chat panel */}
      {chatOpen && (
        <div className="cn-chat-panel">
          <Messages HandleToggleVisibility={() => setChatOpen(false)} />
        </div>
      )}
    </>
  );
};

export default CHeader;